import {
  AlertCreationType,
  AlertValidationForm,
  getAlertDefaultNameKey,
  isFrostAlert,
  PredicateWithOptionalParam
} from '@weenat/client/dist/core/alerts/alerts'
import {
  Alert,
  getPredicateKey,
  Predicate,
  TargetsWithTZ
} from '@weenat/client/dist/resources/alerts.types'
import schemas from '@weenat/client/dist/schemas'
import { TKey, useIntl } from '@weenat/wintl'
import CheckboxField from 'app/src/kit/fields/CheckboxField'
import TextField from 'app/src/kit/fields/TextField'
import SuperForm from 'app/src/kit/SuperForm'
import Text from 'app/src/kit/Text'
import { FormikProps } from 'formik'
import isNil from 'lodash-es/isNil'
import { useMemo, useRef } from 'react'
import CopyToClipBoard from '../kit/CopyToClipBoard'
import Icons from '../kit/Icons'
import AlertCreationStep, { AlertCreationStepProps } from './AlertCreationStep'
import AlertTargetTable from './AlertTargetTable'
import PredicateTable from './PredicateTable'

type AlertValidationProps = AlertCreationStepProps<AlertValidationForm>

interface ValidateAlertProps extends AlertValidationProps {
  alertType: AlertCreationType
  predicates: PredicateWithOptionalParam[]
  targets: TargetsWithTZ
  alertValidationForm: AlertValidationForm
  isNextLoading: boolean
}

const ValidateAlert: FC<ValidateAlertProps> = ({
  alertType,
  onPressNext,
  onPressPrevious,
  predicates,
  targets,
  alertValidationForm,
  ...props
}) => {
  const { t } = useIntl()

  const defaultAlertNameKey = useMemo(
    () => getAlertDefaultNameKey(alertType, getPredicateKey(predicates[0] as Predicate)),
    [alertType, predicates]
  )

  const formRef = useRef<FormikProps<AlertValidationForm>>(null)
  return (
    <AlertCreationStep
      {...props}
      canProgressToNext={props.canProgressToNext || !props.isNextLoading}
      onPressPrevious={() => onPressPrevious?.()}
      onPressNext={() => formRef.current?.submitForm()}
    >
      <Text $fontWeight='bold' $fontSize='md'>
        {t('titles.general_infos')}
      </Text>
      <Box $my='lg'>
        <SuperForm
          schema={schemas.alertCreation.validation}
          initialValues={{
            name: isNil(alertValidationForm.name)
              ? t(
                  defaultAlertNameKey as TKey,
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  {},
                  {
                    capitalize: true
                  }
                )
              : alertValidationForm.name || '',
            isVoiceCallEnabled: !!alertValidationForm.isVoiceCallEnabled,
            isActive: true
          }}
          onSubmit={(values) => {
            onPressNext?.(values)
          }}
          formRef={formRef}
        >
          {({ values }) => (
            <>
              <TextField
                label={t('models.plot.fields.name.label', { capitalize: true })}
                name={'name'}
              />
              {isFrostAlert(predicates) && (
                <CheckboxField
                  label={t('alerts.paramSetups.fieldLabels.enable_voice_call')}
                  name='isVoiceCallEnabled'
                  $hideErrors
                />
              )}
              {isFrostAlert(predicates) && values.isVoiceCallEnabled && (
                <Flex
                  $gap='md'
                  $alignItems='center'
                  $borderRadius={'md'}
                  $backgroundColor={'grayscale.100'}
                  $p='md'
                  $mb='lg'
                >
                  <Icons.MktCheckedPhone $size='lg' />
                  <Flex $gap='sm'>
                    <Text>{t('user_settings.phone_numbers.add_weenat_phone_to_contacts')}</Text>
                    <Text $fontWeight='bold'>
                      <CopyToClipBoard
                        textToCopy={t('weenat.support.alert_phoneNumber')}
                        actionMessageKey='weenat.support.alert_phoneNumber'
                        successMessageKey='actions.copy_success'
                      />
                    </Text>
                  </Flex>
                </Flex>
              )}
            </>
          )}
        </SuperForm>
      </Box>

      <Text $fontWeight='bold' $fontSize='md'>
        {t('alerts.validation.events', { capitalize: true })}
      </Text>
      <Box $my='lg'>
        <PredicateTable isAllowingEdition={false} predicates={predicates as Alert['predicates']} />
      </Box>

      <Text $fontWeight='bold' $fontSize='md'>{`${t('alerts.validation.targets', {
        capitalize: true
      })} (${targets.devices.length + targets.plots.length + targets.stations.length})`}</Text>
      <Box $my='lg'>
        <AlertTargetTable isAllowingEdition={false} isShowingStatuses={false} targets={targets} />
      </Box>
    </AlertCreationStep>
  )
}

export default ValidateAlert
